import { useQuery } from '@tanstack/react-query';
import type { ExpressionSpecification } from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { listProjectCustomData } from '~/api/projectCustomData';
import authStore from '~/authStore';
import type { TOCItem } from '~/common/types';
import { CUSTOM_DATA_RASTER_ANCHOR_ID, HIGHLIGHT_LINE_LAYER_ID } from '~/components2/Map/Widgets/HighlightLayer';
import { persistCustomDataVisibility } from '~/components2/Map/Widgets/Layers/customDataTOC.handlers';
import getConfig from '~/config';
import { useCustomDataRememberVisibility } from '~/hooks/useCustomDataRememberVisibility';
import { customDataQueryKey, customDataRefetchInterval } from '~/pages/projectDetails/filesAndMedia/CustomDataSection';
import layerStore from '~/store2/layers';
import {
  buildProjectRasterTilesUrl,
  buildProjectVectorTilesUrl,
  CUSTOM_DATA_VECTOR_MAX_ZOOM,
  collectCustomDataLeafIds,
  customDataCategoryFilter,
  customDataLayerId,
  customDataToTocItems,
  DEFAULT_CUSTOM_DATA_MAX_ZOOM,
  isCustomDataRowVisible,
  renderableShapefileRows,
  renderableWmtsRows,
} from './customDataLayers.helpers';
import { lineColorMatchExpression } from './customDataSymbology';
import { readCustomDataVisibility, syncCustomDataVisibility } from './customDataVisibility';

const config = getConfig();

// Stable refs so the vector line layers don't churn paint/layout objects each render.
// Width mirrors the fill-outline interpolation in MapLayers.tsx so sparse linework stays
// crisp across zoom; `line-emissive-strength` is required for lines on the v3 basemap.
const VECTOR_LINE_LAYOUT = { 'line-cap': 'round', 'line-join': 'round' } as const;
const VECTOR_LINE_WIDTH: ExpressionSpecification = ['interpolate', ['exponential', 0.5], ['zoom'], 14, 1.5, 20, 3];

// Effect key for the TOC sync: every node of the tree (parent sectionId+label, leaf id+label)
// so a row add/remove, label edit, state change, or category change all re-sync the group.
function serializeTocItems(items: readonly TOCItem[]): string {
  return items
    .map((item) =>
      'children' in item
        ? `${item.sectionId}:${item.label}:[${serializeTocItems(item.children)}]`
        : `${item.id}:${item.label}`,
    )
    .join(',');
}

// Renders project custom-data WMTS rasters from OUR tile-server proxy and registers
// a "Custom Data" TOC group. Ungated — all project members see layers the
// API GET returns (it already filters by role/visibility). Mounted project-wide in
// the map Surface (sibling of FiberLitBuildingsLayer) so the raster + its TOC group
// are available across every tab that shows the map + Layers panel.
export default function CustomDataLayers({ projectId }: { projectId: string }) {
  if (!projectId) return null;
  return <CustomDataLayersInner projectId={projectId} />;
}

function CustomDataLayersInner({ projectId }: { projectId: string }) {
  const { visibleLayerIds, suppressCustomDataDefaultOn, pausePersistence } = useSnapshot(
    layerStore,
  ) as typeof layerStore;
  const { current: map } = MapGL.useMap();
  // Gate raster rendering until the dedicated anchor layer exists, so its `beforeId` resolves.
  const [isAnchorLoaded, setAnchorLoaded] = useState(false);
  // Same gate for vector lines: they render with `beforeId={HIGHLIGHT_LINE_LAYER_ID}`,
  // which react-map-gl only honors once that highlight layer exists.
  const [isHighlightLineLoaded, setHighlightLineLoaded] = useState(false);
  // Ids this mount has already decided, so a refetch or another row appearing can't re-decide them.
  // Per-mount: what survives a reload is the project's localStorage record, which the effect below
  // consults in place of defaulting everything ON.
  const seededIds = useRef<Set<string>>(new Set());
  const rememberEnabled = useCustomDataRememberVisibility();

  // The flag resolves after first paint, by which point every leaf has been decided without it, so
  // re-decide on the way on. On the way off nothing is re-decided — only recording stops — since a
  // turn-off is also the unresolved window of an org switch, which would force layers back ON.
  // Declared above the sync effect so the clear always lands before the ids are read.
  useEffect(() => {
    if (rememberEnabled) seededIds.current.clear();
  }, [rememberEnabled]);

  const { data: rows } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: customDataQueryKey(projectId),
    queryFn: () => listProjectCustomData(projectId),
    // Poll processing→ready even when the files panel (the other poller) isn't mounted.
    refetchInterval: customDataRefetchInterval,
  });

  const wmtsRows = renderableWmtsRows(rows);
  const shapefileRows = renderableShapefileRows(rows);
  // TOC model for the shared "Custom Data" group: flat leaves, except multi-category
  // shapefiles, which become a parent with one leaf per CateTitle.
  const tocItems = customDataToTocItems(rows);
  // The ids that actually drive map visibility: category leaves for split rows, row ids otherwise.
  // Undefined until the query has data — distinct from a project whose rows are genuinely empty,
  // and the state a background refetch error leaves behind while cached rows are still in hand.
  const leafIds = rows === undefined ? undefined : collectCustomDataLeafIds(rows);

  // Sync the TOC group and decide each leaf the first time its id appears: the project's saved
  // record when visibility is remembered, otherwise ON. Skipped while the new-download form owns
  // map visibility (suppressCustomDataDefaultOn) — there the picker selection drives
  // visibleLayerIds directly, and deciding here would render unselected layers.
  // Keyed on the serialized tree so add/remove/label-edit/state-change/category-change re-syncs.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialized tree below
  useEffect(() => {
    const { show, hide, seed, unseed, shouldPersist } = syncCustomDataVisibility({
      leafIds,
      seededIds: seededIds.current,
      saved: rememberEnabled ? readCustomDataVisibility(projectId) : null,
      rememberEnabled,
      suppressDefaultOn: suppressCustomDataDefaultOn,
      pausePersistence,
    });
    for (const id of show) layerStore.visibleLayerIds.add(id);
    for (const id of hide) layerStore.visibleLayerIds.delete(id);
    for (const id of seed) seededIds.current.add(id);
    for (const id of unseed) {
      layerStore.visibleLayerIds.delete(id);
      seededIds.current.delete(id);
    }
    layerStore.setCustomDataTOCGroup(tocItems);
    // After the group is registered, so the record is cut from the same leaves the picker offers.
    if (shouldPersist) persistCustomDataVisibility(projectId);
  }, [
    serializeTocItems(tocItems),
    suppressCustomDataDefaultOn,
    rememberEnabled,
    pausePersistence,
    leafIds === undefined,
  ]);

  // Remove this component's ids + group on unmount so they don't bleed into localStorage
  // or carry over to another project.
  useEffect(() => {
    return () => {
      for (const id of seededIds.current) layerStore.visibleLayerIds.delete(id);
      layerStore.setCustomDataTOCGroup([]);
    };
  }, []);

  // react-map-gl honors `beforeId` only once the anchor layer exists; otherwise Mapbox appends
  // the layer on top. The anchor loads asynchronously with HighlightLayer, so wait for it before
  // rendering — that lands the raster just below the dedicated anchor (above the basemap, below
  // every CG TOC layer) via the declarative `beforeId` on <Layer>, with no imperative reordering.
  useEffect(() => {
    if (!map) return;
    const checkLayers = () => {
      setAnchorLoaded(!!map.getLayer(CUSTOM_DATA_RASTER_ANCHOR_ID));
      setHighlightLineLoaded(!!map.getLayer(HIGHLIGHT_LINE_LAYER_ID));
    };
    checkLayers();
    map.on('styledata', checkLayers);
    return () => {
      map.off('styledata', checkLayers);
    };
  }, [map]);

  return (
    <>
      {isAnchorLoaded &&
        wmtsRows.map((row) => {
          const layerId = customDataLayerId(row.id);
          if (!visibleLayerIds.has(layerId)) return null;
          // Built exactly like FiberLitBuildingsLayer: our proxy URL + the JWT, so the
          // upstream WMTS token never reaches the client.
          const tilesUrl = buildProjectRasterTilesUrl({
            tileServerBase: config.tileServerBase,
            projectId,
            rowId: row.id,
            token: authStore.token ?? '',
          });
          return (
            <MapGL.Source
              key={layerId}
              id={layerId}
              type="raster"
              tiles={[tilesUrl]}
              tileSize={256}
              maxzoom={row.maxZoom ?? DEFAULT_CUSTOM_DATA_MAX_ZOOM}
            >
              <MapGL.Layer
                id={layerId}
                source={layerId}
                type="raster"
                paint={{ 'raster-opacity': 0.9, 'raster-emissive-strength': 1 }}
                beforeId={CUSTOM_DATA_RASTER_ANCHOR_ID}
              />
            </MapGL.Source>
          );
        })}
      {isHighlightLineLoaded &&
        shapefileRows.map((row) => {
          const layerId = customDataLayerId(row.id);
          if (!isCustomDataRowVisible(row, visibleLayerIds)) return null;
          // Non-null only when some of the row's categories are toggled off; then it ANDs onto
          // each geometry-type filter, so the all-on case keeps today's filters byte-identical.
          const categoryFilter = customDataCategoryFilter(row, visibleLayerIds);
          const withCategoryFilter = (filter: ExpressionSpecification): ExpressionSpecification =>
            categoryFilter ? ['all', filter, categoryFilter] : filter;
          // Our proxy MVT URL + the JWT, so the upstream token never reaches the client.
          const tilesUrl = buildProjectVectorTilesUrl({
            tileServerBase: config.tileServerBase,
            projectId,
            rowId: row.id,
            token: authStore.token ?? '',
          });
          return (
            <MapGL.Source
              key={layerId}
              id={layerId}
              type="vector"
              tiles={[tilesUrl]}
              maxzoom={CUSTOM_DATA_VECTOR_MAX_ZOOM}
            >
              {/* All three layers use source-layer "customData" (the backend MVT layer name); a mismatch silently renders nothing. */}
              {/* Polygon fills (semi-transparent so the basemap shows through). */}
              <MapGL.Layer
                id={`${layerId}-fill`}
                source={layerId}
                source-layer="customData"
                type="fill"
                filter={withCategoryFilter(['==', ['geometry-type'], 'Polygon'])}
                paint={{
                  'fill-color': lineColorMatchExpression(row.categories),
                  'fill-opacity': 0.3,
                }}
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
              {/* Lines + polygon outlines. Keeps id={layerId} so existing inspect/visibility wiring is intact.
                  `lineColorMatchExpression` is geometry-agnostic — it builds a `match` on CateTitle. */}
              <MapGL.Layer
                id={layerId}
                source={layerId}
                source-layer="customData"
                type="line"
                filter={withCategoryFilter([
                  'any',
                  ['==', ['geometry-type'], 'LineString'],
                  ['==', ['geometry-type'], 'Polygon'],
                ])}
                layout={VECTOR_LINE_LAYOUT}
                paint={{
                  'line-color': lineColorMatchExpression(row.categories),
                  'line-width': VECTOR_LINE_WIDTH,
                  'line-emissive-strength': 1,
                }}
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
              {/* Points as colored circles with a white stroke for contrast (no icon system yet — signs = v2). */}
              <MapGL.Layer
                id={`${layerId}-circle`}
                source={layerId}
                source-layer="customData"
                type="circle"
                filter={withCategoryFilter(['==', ['geometry-type'], 'Point'])}
                paint={{
                  'circle-radius': 5,
                  'circle-color': lineColorMatchExpression(row.categories),
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#6e7376',
                  'circle-emissive-strength': 1,
                }}
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
            </MapGL.Source>
          );
        })}
    </>
  );
}
